import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 768px)').matches);

  // Refs for persistent DOM elements
  const bakaRef = useRef(null);
  const desktopTargetRef = useRef(null);
  const mobileTargetRef = useRef(null);
  const headerWrapperRef = useRef(null);

  // Refs for searchbar
  const searchBarRef = useRef(null);
  const searchBarDesktopTargetRef = useRef(null);
  const searchBarMobileTargetRef = useRef(null);
  const mobileTopControlsTargetRef = useRef(null);

  const [isCartEmpty, setIsCartEmpty] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };

    window.addEventListener('resize', handleResize);

    // Monitor Baka Cart content
    const observer = new MutationObserver(() => {
      const cartEl = bakaRef.current?.querySelector('baka-shopping-cart');
      if (cartEl) {
        const hasContent = cartEl.innerHTML.trim().length > 0 && !cartEl.innerHTML.includes('0,00');
        setIsCartEmpty(!hasContent);
      }
    });

    if (bakaRef.current) {
      observer.observe(bakaRef.current, { childList: true, subtree: true, characterData: true });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('is-menu-open', isMenuOpen);
    return () => document.body.classList.remove('is-menu-open');
  }, [isMenuOpen]);

  useLayoutEffect(() => {
    const el = headerWrapperRef.current;
    if (!el) return;

    const setVar = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--mobile-header-height', `${height}px`);
    };

    setVar();

    const onResize = () => requestAnimationFrame(setVar);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(setVar) : null;
    ro?.observe(el);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    const categoryObserver = { current: null };

    const transformMenu = () => {
      const container = document.querySelector('.nav-categories');
      if (!container) return;

      const bakaCategories = container.querySelector('baka-categories');
      if (!bakaCategories) return;

      const getItems = (root) => {
        let items = Array.from(root.querySelectorAll('[class*="mobile-menu-item"]'));
        if (items.length === 0 && root.shadowRoot) {
          items = Array.from(root.shadowRoot.querySelectorAll('[class*="mobile-menu-item"]'));
        }
        return items;
      };

      const items = getItems(bakaCategories);
      if (items.length === 0) return;

      categoryObserver.current?.disconnect();

      const rootTree = {};
      const stack = [{ depth: -1, children: rootTree }];

      items.forEach(item => {
        const link = item.querySelector('a');
        if (!link) return;

        const depthMatch = item.className.match(/depth-(\d+)/);
        const depth = depthMatch ? parseInt(depthMatch[1], 10) : 0;

        const node = {
          name: link.textContent.trim(),
          element: link.cloneNode(true),
          children: {}
        };

        while (stack.length > 1 && stack[stack.length - 1].depth >= depth) {
          stack.pop();
        }

        const parent = stack[stack.length - 1];
        parent.children[node.name] = node;
        stack.push({ depth: depth, children: node.children });
      });

      const tree = rootTree;

      const renderTree = (node, parentElement, depth = 0) => {
        Object.keys(node).forEach(key => {
          const item = node[key];
          const hasChildren = Object.keys(item.children).length > 0;

          const wrapper = document.createElement('div');
          wrapper.className = `nav-item-wrapper depth-${depth}`;

          const link = item.element || document.createElement('a');
          if (!item.element) {
            link.textContent = item.name;
            link.href = `/webshop/category/${item.fullPath}`;
            link.className = 'nav-link-placeholder';
          }
          link.classList.add('nav-link-main');

          if (hasChildren) {
            wrapper.classList.add('has-children');
            const arrow = document.createElement('span');
            arrow.className = 'nav-arrow-icon';
            arrow.innerHTML = '<svg width="12" height="12" viewBox="0 0 320 512"><path fill="currentColor" d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"></path></svg>';
            link.appendChild(arrow);

            link.onclick = (e) => {
              if (window.matchMedia('(max-width: 768px)').matches) {
                e.preventDefault(); e.stopPropagation();
                wrapper.classList.add('is-open');
              }
            };
          }

          wrapper.appendChild(link);

          if (hasChildren) {
            const submenu = document.createElement('div');
            submenu.className = 'nav-submenu';

            const backBtn = document.createElement('div');
            backBtn.className = 'nav-back-button';
            backBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 320 512"><path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s-12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"></path></svg><span>Terug</span>';

            const header = document.createElement('div');
            header.className = 'nav-submenu-header';
            header.appendChild(backBtn);
            const titleEl = document.createElement('div');
            titleEl.className = 'nav-submenu-title';
            titleEl.textContent = item.name;
            header.appendChild(titleEl);
            submenu.appendChild(header);

            backBtn.onclick = (e) => {
              e.preventDefault(); e.stopPropagation();
              wrapper.classList.remove('is-open');
            };

            renderTree(item.children, submenu, depth + 1);
            wrapper.appendChild(submenu);
          }
          parentElement.appendChild(wrapper);
        });
      };

      let menuOverlay = container.querySelector('.nav-menu-custom-overlay');
      if (!menuOverlay) {
        menuOverlay = document.createElement('div');
        menuOverlay.className = 'nav-menu-custom-overlay';
        container.appendChild(menuOverlay);
      }
      menuOverlay.innerHTML = '';

      bakaCategories.classList.add('hide-on-mobile-custom');
      renderTree(tree, menuOverlay);
      categoryObserver.current?.observe(bakaCategories, { childList: true, subtree: true });
    };

    categoryObserver.current = new MutationObserver((mutations) => {
      const shouldTransform = mutations.some(m =>
        Array.from(m.addedNodes).some(node =>
          node.nodeType === 1 && (node.matches('a') || node.querySelector('a'))
        )
      );
      if (shouldTransform) {
        transformMenu();
      }
    });

    const target = document.querySelector('.nav-categories');
    if (target) {
      categoryObserver.current.observe(target, { childList: true, subtree: true });
    }

    transformMenu();

    return () => {
      categoryObserver.current?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const el = bakaRef.current;
    const desktop = desktopTargetRef.current;
    const mobile = mobileTargetRef.current;
    const mobileTop = mobileTopControlsTargetRef.current;

    if (!el || !desktop || !mobile || !mobileTop) return;

    // Mobile + menu open: move account/cart to the top bar (replacing the searchbar spot).
    // Otherwise keep them in the normal desktop target (also used for mobile when menu is closed).
    const target = (isMobile && isMenuOpen) ? mobileTop : desktop;

    if (el.parentNode !== target) {
      target.appendChild(el);
    }
  }, [isMobile, isMenuOpen]);

  useLayoutEffect(() => {
    const el = searchBarRef.current;
    const desktopTarget = searchBarDesktopTargetRef.current;
    const mobileTarget = searchBarMobileTargetRef.current;
    if (!el || !desktopTarget || !mobileTarget) return;

    const relocate = () => {
      // When the mobile menu is open we don't want the searchbar in the header row;
      // it gets replaced by the account/cart buttons to keep the menu compact.
      if (window.matchMedia('(max-width: 768px)').matches && isMenuOpen) {
        if (el.parentNode !== desktopTarget) desktopTarget.appendChild(el);
        return;
      }

      const mobileVisible = window.getComputedStyle(mobileTarget).display !== 'none';
      const desktopVisible = window.getComputedStyle(desktopTarget).display !== 'none';
      const target = mobileVisible && !desktopVisible ? mobileTarget : desktopTarget;
      if (el.parentNode !== target) target.appendChild(el);
    };

    relocate();
    const onResize = () => requestAnimationFrame(relocate);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const wrapperClass = `header-wrapper alt ${isMenuOpen ? 'menu-open' : ''}`;
  const headerClass = "navbar w-nav alt";

  return (
    <div className={wrapperClass} ref={headerWrapperRef}>
      <div
        data-animation="default"
        data-collapse="medium"
        data-duration="400"
        data-easing="ease"
        data-easing2="ease"
        role="banner"
        className={headerClass}
        style={{ backgroundColor: 'transparent' }}
      >
        {/* Top Bar */}
        <div className="top-bar">
          <div className="container">
            <div className="column-stretch">
              <div className="column">
                <div>
                  <a className="link" href="tel:0031162461175">
                    0031 (0)162 - 46 11 75
                  </a>
                </div>
                <div className="footer-13-divider dark"></div>
                <div>Snelle levering - geen kleine orderkosten!</div>
              </div>
              <div className="mini-nav">
                <Link to="/webshop/account" className="link login-trigger" reloadDocument>Mijn account</Link>
                <Link to="/about" className="link">Over ons</Link>
                <Link to="/contact" className="link">Contact</Link>
                <a href="#" className="link">FAQs</a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="container nav-copy-copy">
          <div className="nav-brand_searchbar">
            <a href="/" className="w-nav-brand">
              <picture>
                <source
                  srcSet="/media/favicon/websites/1/favicon.png"
                  media="(max-width: 768px)"
                />
                <img
                  src="/media/logo/websites/1/logo-large.png"
                  loading="lazy"
                  alt="Timmers"
                  className="image"
                />
              </picture>
            </a>

            {/* Mobile: inline search between logo and hamburger */}
            <div
              className="search-bar-container-mobile"
              aria-label="Zoeken"
              ref={searchBarMobileTargetRef}
            ></div>

            {/* Mobile menu open: move account/cart here to replace the searchbar */}
            <div
              className="mobile-top-controls-container"
              aria-label="Account en winkelwagen"
              ref={mobileTopControlsTargetRef}
            ></div>

            <nav
              role="navigation"
              className={`nav-menu w-nav-menu ${isMenuOpen ? 'w--open' : ''}`}
              id="mainMenu"
            ></nav>

            <div
              className={`w-nav-button ${isMenuOpen ? 'w--open' : ''}`}
              id="menu-toggle"
              onClick={toggleMenu}
              aria-label="menu"
              role="button"
            >
              {isMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 384 512" style={{ color: 'var(--timmers-yellow)' }}>
                  <path fill="currentColor" d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z"></path>
                </svg>
              ) : (
                <div className="w-icon-nav-menu"></div>
              )}
            </div>
          </div>

          {/* Desktop searchbar target */}
          <div className="search-bar-container-desktop" ref={searchBarDesktopTargetRef}></div>

          {/* Persistent searchbar node (moved between desktop/mobile targets) */}
          <div ref={searchBarRef} className="search-bar-mount">
            <baka-searching-bar className="search-bar-unified"></baka-searching-bar>
          </div>

          <div className="header-controls" ref={desktopTargetRef}>
            {/* Initially render them here, JS will move them if mobile */}
            <div ref={bakaRef} className={`baka-controls-wrapper ${isCartEmpty ? 'is-cart-empty' : ''}`}>
              <baka-account></baka-account>
              <baka-shopping-cart></baka-shopping-cart>
            </div>
          </div>
        </div>

        {/* Category Navigation (also serves as mobile menu) */}
          <div className={`div-block-224-copy ${isMenuOpen ? 'is-open' : ''}`}>
          <div className="container">
            {/* Kept for backwards compatibility; no longer used for layout */}
            <div className="header-controls header-controls-mobile header-controls-in-menu" ref={mobileTargetRef}></div>

            <div className="nav-categories">
              <baka-categories RowMode="true"></baka-categories>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
